import { useState } from 'react';
import { Button, Input, Wordmark } from '@novaquantis/design-system';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(loginIdentifier, password);
      } else {
        await register(username, registerEmail, password);
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'An error occurred',
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError('');
    setLoginIdentifier('');
    setUsername('');
    setRegisterEmail('');
    setPassword('');
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 bg-background bg-[image:var(--nq-canvas-glow)] px-10 py-10">
      <div className="flex flex-col items-center gap-3">
        <img
          src="/assets/logo-bull-trim.png"
          alt="NovaQuantis"
          className="h-[72px] w-auto drop-shadow-[0_8px_24px_rgba(37,180,224,0.3)]"
        />
        <Wordmark size={28} />
      </div>

      <div className="w-full max-w-[420px] rounded-2xl border border-[color:var(--border-slate)] bg-[color:var(--nq-surface-card-2)] px-11 py-9 shadow-[var(--shadow-card-soft),var(--inset-card-glow)]">
        <div className="mb-5 text-center">
          <h1 className="mb-2 font-display text-2xl font-bold tracking-tight text-secondary [text-shadow:var(--glow-gold-text)]">
            {isLogin ? 'Login' : 'Register'}
          </h1>
          <p className="text-sm leading-normal text-[color:var(--text-secondary)]">
            {isLogin
              ? 'Sign in to access your NovaQuantis portfolio.'
              : 'Create an account to get started.'}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          autoComplete="on"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.requestSubmit();
            }
          }}
        >
          {isLogin ? (
            <Input
              label="Email or username"
              id="login-identifier"
              name="username"
              type="text"
              value={loginIdentifier}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLoginIdentifier(e.target.value)
              }
              autoComplete="username"
              required
              placeholder="Email or username"
              disabled={loading}
              variant="onDark"
            />
          ) : (
            <>
              <Input
                label="Username"
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUsername(e.target.value)
                }
                autoComplete="username"
                required
                placeholder="Choose a username"
                disabled={loading}
                variant="onDark"
              />
              <Input
                label="Email"
                id="register-email"
                name="email"
                type="email"
                value={registerEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRegisterEmail(e.target.value)
                }
                autoComplete="email"
                required
                placeholder="Enter your email"
                disabled={loading}
                variant="onDark"
              />
            </>
          )}

          <Input
            label="Password"
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            required
            placeholder="Enter your password"
            minLength={6}
            disabled={loading}
            variant="onDark"
          />

          {error && (
            <div className="mb-4 rounded-md border border-negative/35 bg-negative/15 px-4 py-3 text-sm text-[color:var(--nq-down-soft)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} fullWidth variant="glow">
            {loading ? 'Processing…' : isLogin ? 'Login' : 'Register'}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              resetForm();
            }}
            className="cursor-pointer appearance-none border-0 bg-transparent p-0 font-[inherit] text-[color:var(--nq-slate-500)] underline underline-offset-[3px] transition-colors hover:text-[color:var(--nq-blue-bright)]"
          >
            {isLogin ? 'Register' : 'Login'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
